import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useAppData } from "../context/AppDataContext";
import api from "../services/api";
import {
  FiX,
  FiChevronRight,
  FiChevronDown,
  FiPlusCircle,
  FiLogOut,
  FiSearch,
  FiShoppingBag,
  FiZap,
  FiTrendingUp,
  FiShield,
  FiPackage,
  FiMessageSquare,
  FiHeart,
  FiShoppingCart,
  FiUser,
} from "react-icons/fi";
import { localizedCategoryName } from "../utils/i18nContent";

const MobileDrawer = ({ isOpen, onClose, categories = [] }) => {
  const { t, i18n } = useTranslation();
  const { user, logout, isAuthenticated } = useAuth();
  const { unreadMessages, favoritesCount, cartCount } = useAppData() || {};
  const navigate = useNavigate();

  // Search state
  const [searchQuery, setSearchQuery] = useState("");

  // Category accordion state
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Gesture / Touch drag to dismiss state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartY = useRef(0);
  const currentDragY = useRef(0);

  // Lock body scroll when bottom sheet is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      setDragY(0);
    } else {
      document.body.style.overflow = "unset";
      setDragY(0);
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    const deltaY = e.touches[0].clientY - touchStartY.current;
    if (deltaY > 0) {
      // Dragging downward
      currentDragY.current = deltaY;
      setDragY(deltaY);
    } else {
      // Elastic resistance dragging upward
      currentDragY.current = deltaY * 0.15;
      setDragY(deltaY * 0.15);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (currentDragY.current > 75) {
      onClose();
    }
    setDragY(0);
    currentDragY.current = 0;
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      onClose();
      navigate(`/search/?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
    }
  };

  const handleLanguageChange = async (lng) => {
    const normalized = lng === "fr" ? "fr" : "en";
    i18n.changeLanguage(lng);
    localStorage.setItem("chafadi-lang", normalized);
    const token = localStorage.getItem("chafadi-token");
    if (token) {
      try {
        await api.patch("/me/language/", { language: normalized });
      } catch (_) {}
    }
  };

  const handleLogout = () => {
    onClose();
    logout();
  };

  const toggleCategory = (catId) => {
    setExpandedCategory((prev) => (prev === catId ? null : catId));
  };

  const profileImage =
    user?.supplier_profile?.logo_url ||
    user?.buyer_profile?.logo_url ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(
      (user?.first_name || "C") + " " + (user?.last_name || "U")
    )}&background=ff6a00&color=fff&size=100&bold=true`;

  return (
    <>
      {/* ── 1. FROSTED BACKDROP ── */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-md z-[9999] lg:hidden transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* ── 2. APPLE-GRADE BOTTOM SHEET (Slides from Bottom with Native Drag Physics) ── */}
      <div
        className={`fixed inset-x-0 bottom-0 z-[10000] lg:hidden bg-[var(--color-bg)] text-[var(--color-text-primary)] rounded-t-[32px] border-t border-[var(--color-border)] shadow-[0_-20px_60px_rgba(0,0,0,0.22)] max-h-[90vh] flex flex-col will-change-transform ${
          isOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
        style={{
          transform: isOpen
            ? `translateY(${Math.max(0, dragY)}px)`
            : "translateY(100%)",
          transition: isDragging
            ? "none"
            : "transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)",
        }}
        role="dialog"
        aria-modal="true"
      >
        {/* ── TACTILE DRAG / PULL HANDLE ── */}
        <div
          className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none select-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={onClose}
        >
          <div className="w-12 h-1.5 rounded-full bg-[var(--color-border)] hover:bg-[var(--color-text-tertiary)] active:scale-95 transition-all" />
        </div>

        {/* ── SHEET HEADER: User / Guest Greeting ── */}
        <div
          className="flex items-center justify-between px-5 pb-3 border-b border-[var(--color-border)] shrink-0 touch-none select-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {isAuthenticated && user ? (
            <Link
              to="/profile"
              onClick={onClose}
              className="flex items-center gap-3 no-underline min-w-0 group"
            >
              <div className="relative shrink-0">
                <img
                  src={profileImage}
                  className="w-10 h-10 rounded-full object-cover border border-[var(--color-border)] shadow-sm"
                  alt=""
                />
                <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-[var(--color-bg)]" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider leading-none">
                    {user?.is_supplier ? t("common.supplier", "Supplier") : t("navbar.authenticated", "Signed In")}
                  </span>
                  <FiChevronRight size={12} className="text-[var(--color-text-tertiary)] group-hover:text-[var(--color-accent)] transition-colors" />
                </div>
                <h4 className="text-sm font-semibold text-[var(--color-text-primary)] truncate group-hover:text-[var(--color-accent)] transition-colors">
                  {user.first_name} {user.last_name}
                </h4>
              </div>
            </Link>
          ) : (
            <div className="flex items-center gap-2.5">
              <img src="/logo.png" alt="Chafadi" className="h-7 w-auto object-contain" />
              <div>
                <span className="text-xs font-semibold text-[var(--color-text-primary)] block leading-tight">
                  Chafadi Marketplace
                </span>
                <span className="text-[10px] text-[var(--color-text-tertiary)]">
                  Niger &amp; West Africa
                </span>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              aria-label="Close menu"
              className="w-8 h-8 flex items-center justify-center rounded-full bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] border border-[var(--color-border)] active:scale-95 transition-all"
            >
              <FiX size={15} />
            </button>
          </div>
        </div>

        {/* ── SCROLLABLE SHEET CONTENT ── */}
        <div
          className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-5 py-4 space-y-5"
          style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
        >
          {/* 1. Integrated Spotlight Search Bar */}
          <form onSubmit={handleSearchSubmit} className="relative">
            <div className="flex items-center bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-full px-3.5 h-11 focus-within:border-[var(--color-accent)] focus-within:ring-2 focus-within:ring-[var(--color-accent-subtle)] transition-all">
              <FiSearch className="text-[var(--color-text-tertiary)] shrink-0 mr-2.5" size={16} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("navbar.searchPlaceholder", "Search products, categories, suppliers...")}
                className="w-full bg-transparent border-none outline-none text-xs text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)]"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] p-1 shrink-0"
                >
                  <FiX size={13} />
                </button>
              )}
            </div>
          </form>

          {/* 2. Apple iOS Control Center Grid Hub */}
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <span className="text-[10px] font-semibold text-[var(--color-text-tertiary)] uppercase tracking-wider">
                {t("common.explore", "Explore Chafadi")}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              {/* Shop All */}
              <Link
                to="/search/"
                onClick={onClose}
                className="flex items-center gap-3 p-3 rounded-[18px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-primary)] hover:border-[var(--color-accent)] active:scale-[0.98] transition-all no-underline"
              >
                <div className="w-8 h-8 rounded-full bg-[var(--color-accent-subtle)] text-[var(--color-accent)] flex items-center justify-center shrink-0">
                  <FiShoppingBag size={15} />
                </div>
                <div className="min-w-0">
                  <span className="text-xs font-semibold block truncate leading-tight">
                    {t("navbar.shop", "Shop All")}
                  </span>
                  <span className="text-[10px] text-[var(--color-text-tertiary)] truncate block">
                    {t("navbar.browseAll", "Full Catalog")}
                  </span>
                </div>
              </Link>

              {/* New Drops */}
              <Link
                to="/new-arrivals"
                onClick={onClose}
                className="flex items-center gap-3 p-3 rounded-[18px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-primary)] hover:border-[var(--color-accent)] active:scale-[0.98] transition-all no-underline"
              >
                <div className="w-8 h-8 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
                  <FiZap size={15} />
                </div>
                <div className="min-w-0">
                  <span className="text-xs font-semibold block truncate leading-tight">
                    {t("navbar.newArrivals", "New Drops")}
                  </span>
                  <span className="text-[10px] text-[var(--color-text-tertiary)] truncate block">
                    {t("navbar.latest", "Fresh Listings")}
                  </span>
                </div>
              </Link>

              {/* Flash Deals */}
              <Link
                to="/flash-deals"
                onClick={onClose}
                className="flex items-center gap-3 p-3 rounded-[18px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-primary)] hover:border-[var(--color-accent)] active:scale-[0.98] transition-all no-underline"
              >
                <div className="w-8 h-8 rounded-full bg-rose-500/15 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0">
                  <FiTrendingUp size={15} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-semibold truncate leading-tight">
                      {t("navbar.flashDeals", "Flash Deals")}
                    </span>
                    <span className="text-[9px] font-bold px-1 py-0.5 rounded-full bg-rose-500/20 text-rose-600 dark:text-rose-400 leading-none">
                      %
                    </span>
                  </div>
                  <span className="text-[10px] text-[var(--color-text-tertiary)] truncate block">
                    {t("navbar.discounts", "Discounts")}
                  </span>
                </div>
              </Link>

              {/* Sell on Chafadi */}
              <Link
                to="/products/add"
                onClick={onClose}
                className="flex items-center gap-3 p-3 rounded-[18px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-primary)] hover:border-[var(--color-accent)] active:scale-[0.98] transition-all no-underline"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#ff9f0a] to-[#ff5e00] text-white flex items-center justify-center shrink-0 shadow-sm">
                  <FiPlusCircle size={16} />
                </div>
                <div className="min-w-0">
                  <span className="text-xs font-semibold block truncate leading-tight">
                    {t("navbar.sell", "Sell Product")}
                  </span>
                  <span className="text-[10px] text-[var(--color-text-tertiary)] truncate block">
                    {t("navbar.postAsset", "Post an Ad")}
                  </span>
                </div>
              </Link>

              {/* My Orders / Track */}
              <Link
                to={isAuthenticated ? "/profile" : "/login"}
                onClick={onClose}
                className="flex items-center gap-3 p-3 rounded-[18px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-primary)] hover:border-[var(--color-accent)] active:scale-[0.98] transition-all no-underline"
              >
                <div className="w-8 h-8 rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                  <FiPackage size={15} />
                </div>
                <div className="min-w-0">
                  <span className="text-xs font-semibold block truncate leading-tight">
                    {t("profile.orders", "My Orders")}
                  </span>
                  <span className="text-[10px] text-[var(--color-text-tertiary)] truncate block">
                    {t("common.track", "Track & Delivery")}
                  </span>
                </div>
              </Link>

              {/* Messages / Chat */}
              <Link
                to={isAuthenticated ? "/chat" : "/login"}
                onClick={onClose}
                className="flex items-center gap-3 p-3 rounded-[18px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-primary)] hover:border-[var(--color-accent)] active:scale-[0.98] transition-all no-underline"
              >
                <div className="relative w-8 h-8 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                  <FiMessageSquare size={15} />
                  {unreadMessages > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center">
                      {unreadMessages > 9 ? "9+" : unreadMessages}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <span className="text-xs font-semibold block truncate leading-tight">
                    {t("navbar.myMessages", "Messages")}
                  </span>
                  <span className="text-[10px] text-[var(--color-text-tertiary)] truncate block">
                    {unreadMessages > 0 ? `${unreadMessages} unread` : t("common.chatNow", "Direct Chat")}
                  </span>
                </div>
              </Link>
            </div>

            {/* Quick Quick Access Badges (Wishlist & Cart) */}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <Link
                to="/favorites"
                onClick={onClose}
                className="flex items-center justify-between px-3.5 py-2.5 rounded-[14px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-xs text-[var(--color-text-primary)] no-underline hover:border-[var(--color-accent)] transition-all"
              >
                <div className="flex items-center gap-2">
                  <FiHeart size={14} className="text-rose-500" />
                  <span className="font-medium text-[11px]">{t("navbar.favorites", "Wishlist")}</span>
                </div>
                {favoritesCount > 0 && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-600 dark:text-rose-400">
                    {favoritesCount}
                  </span>
                )}
              </Link>

              <Link
                to="/cart"
                onClick={onClose}
                className="flex items-center justify-between px-3.5 py-2.5 rounded-[14px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-xs text-[var(--color-text-primary)] no-underline hover:border-[var(--color-accent)] transition-all"
              >
                <div className="flex items-center gap-2">
                  <FiShoppingCart size={14} className="text-[var(--color-accent)]" />
                  <span className="font-medium text-[11px]">{t("navbar.cart", "Cart")}</span>
                </div>
                {cartCount > 0 && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[var(--color-accent-subtle)] text-[var(--color-accent)]">
                    {cartCount}
                  </span>
                )}
              </Link>
            </div>
          </div>

          {/* 3. WhatsApp Concierge VIP Banner */}
          <a
            href="https://wa.me/905058186932"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3.5 p-3.5 rounded-[20px] bg-emerald-500/10 border border-emerald-500/20 no-underline transition-all hover:bg-emerald-500/15 active:scale-[0.99]"
          >
            <div className="w-10 h-10 shrink-0 bg-emerald-500 text-white rounded-2xl flex items-center justify-center shadow-md shadow-emerald-500/25">
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.72.937 3.659 1.432 5.631 1.433h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <h4 className="text-xs font-semibold text-[var(--color-text-primary)]">
                  {t("navbar.whatsapp", "WhatsApp Concierge")}
                </h4>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">
                  Online
                </span>
              </div>
              <p className="text-[10px] text-[var(--color-text-secondary)] mt-0.5 leading-tight">
                {t("navbar.whatsappSupport", "Direct assistance, order tracking & verified sellers")}
              </p>
            </div>
          </a>

          {/* 4. Interactive Category Explorer (Apple Grouped Accordion) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <span className="text-[10px] font-semibold text-[var(--color-text-tertiary)] uppercase tracking-wider">
                {t("navbar.categories", "Browse Categories")}
              </span>
              <Link
                to="/search"
                onClick={onClose}
                className="text-[11px] font-semibold text-[var(--color-accent)] hover:underline"
              >
                {t("common.seeAll", "View All")} &rsaquo;
              </Link>
            </div>

            <div className="rounded-[22px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] overflow-hidden divide-y divide-[var(--color-border)]">
              {categories.slice(0, 8).map((cat) => {
                const isExpanded = expandedCategory === cat.id;
                const catName = localizedCategoryName(cat, i18n.language);
                const hasChildren = cat.children && cat.children.length > 0;

                return (
                  <div key={cat.id} className="transition-colors">
                    <div className="flex items-center justify-between p-3.5">
                      <Link
                        to={`/category/${cat.id}/${encodeURIComponent(catName)}`}
                        className="flex-1 text-xs font-semibold text-[var(--color-text-primary)] no-underline truncate hover:text-[var(--color-accent)] transition-colors"
                        onClick={onClose}
                      >
                        {catName}
                      </Link>

                      {hasChildren && (
                        <button
                          type="button"
                          onClick={() => toggleCategory(cat.id)}
                          aria-label={`Toggle ${catName}`}
                          className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text-primary)] active:scale-95 transition-all ml-1 shrink-0"
                        >
                          <FiChevronDown
                            size={15}
                            className={`transition-transform duration-200 ${
                              isExpanded ? "rotate-180 text-[var(--color-accent)]" : ""
                            }`}
                          />
                        </button>
                      )}
                    </div>

                    {hasChildren && isExpanded && (
                      <div className="px-3.5 pb-3.5 pt-0">
                        <div className="flex flex-wrap gap-1.5 p-2 rounded-[16px] bg-[var(--color-bg)] border border-[var(--color-border)]">
                          {cat.children.map((child) => {
                            const childName = localizedCategoryName(child, i18n.language);
                            return (
                              <Link
                                key={child.id}
                                to={`/category/${child.id}/${encodeURIComponent(childName)}`}
                                className="text-[10px] font-medium text-[var(--color-text-secondary)] no-underline bg-[var(--color-bg-secondary)] px-2.5 py-1 rounded-full border border-[var(--color-border)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] transition-all"
                                onClick={onClose}
                              >
                                {childName}
                              </Link>
                            );
                          })}
                          <Link
                            to={`/category/${cat.id}/${encodeURIComponent(catName)}`}
                            className="text-[10px] font-semibold text-[var(--color-accent)] no-underline px-2.5 py-1 hover:underline"
                            onClick={onClose}
                          >
                            {t("common.viewAllIn", "All in")} {catName} &rsaquo;
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 5. Language Selector (EN / FR with flags) */}
          <div className="p-3.5 rounded-[22px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] space-y-2.5">
            <div className="flex items-center justify-between px-1">
              <span className="text-[10px] font-semibold text-[var(--color-text-tertiary)] uppercase tracking-wider block">
                {t("common.language", "Language")}
              </span>
              <span className="text-[10px] text-[var(--color-accent)] font-semibold uppercase">
                {i18n.language.startsWith("fr") ? "Français" : "English"}
              </span>
            </div>

            {/* Segmented Language Buttons with Flags */}
            <div className="p-1 rounded-full bg-[var(--color-bg)] border border-[var(--color-border)] flex items-center justify-between gap-1">
              <button
                type="button"
                onClick={() => handleLanguageChange("fr")}
                className={`flex-1 py-2 rounded-full text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                  i18n.language.startsWith("fr")
                    ? "bg-[var(--color-accent)] text-white shadow-sm"
                    : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                }`}
              >
                <span className="text-sm">🇫🇷</span>
                <span>Français</span>
              </button>
              <button
                type="button"
                onClick={() => handleLanguageChange("en")}
                className={`flex-1 py-2 rounded-full text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                  !i18n.language.startsWith("fr")
                    ? "bg-[var(--color-accent)] text-white shadow-sm"
                    : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                }`}
              >
                <span className="text-sm">🇬🇧</span>
                <span>English</span>
              </button>
            </div>
          </div>
        </div>

        {/* ── 3. SHEET FOOTER: Safe Area Padding & Security Guarantee ── */}
        <div className="px-5 py-3.5 border-t border-[var(--color-border)] bg-[var(--color-bg)] shrink-0 pb-[max(env(safe-area-inset-bottom),18px)] space-y-2.5">
          {isAuthenticated ? (
            <button
              onClick={handleLogout}
              className="w-full flex h-11 items-center justify-center gap-2 bg-red-500/10 border border-red-500/20 rounded-full text-xs font-semibold text-red-600 dark:text-red-400 active:scale-[0.98] transition-all"
            >
              <FiLogOut size={15} />
              <span>{t("auth.logout", "Log Out")}</span>
            </button>
          ) : (
            <div className="grid grid-cols-2 gap-2.5">
              <Link
                to="/login"
                className="flex h-11 items-center justify-center gap-2 text-xs font-bold rounded-full bg-gradient-to-r from-[#ff9f0a] to-[#ff5e00] text-white shadow-[0_4px_16px_rgba(255,106,0,0.35)] active:scale-98 transition-all no-underline"
                onClick={onClose}
              >
                <FiUser size={14} />
                <span>{t("auth.login", "Sign In")}</span>
              </Link>
              <Link
                to="/login?mode=signup"
                className="flex h-11 items-center justify-center text-xs font-bold rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] hover:border-[var(--color-accent)] text-[var(--color-text-primary)] active:scale-98 transition-all no-underline"
                onClick={onClose}
              >
                {t("auth.register", "Register")}
              </Link>
            </div>
          )}

          <div className="flex items-center justify-between text-[10px] text-[var(--color-text-tertiary)] px-1">
            <span>Chafadi &copy; 2026</span>
            <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold">
              <FiShield size={11} /> Escrow Protected
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileDrawer;
